// AI-generated: please review with /code-review before committing.
import {
  PlanType,
  SubscriptionStatus,
} from '@showbie-socrative/socrative-utils';
import { isAfter } from 'date-fns';

import { Subscription } from '../../../../api/subscription';

/**
 * Derives the UI subscription state from the raw DB fields returned by the
 * backend. B2B subscriptions (Organization / Complete) are never auto-expired
 * by Stripe webhooks, so their `status` column stays `active` past `end_date`.
 * `end_date` is therefore the authoritative "is this still paid?" signal and
 * is checked up front, independent of `status`.
 *
 * Full mapping (see also subscriptionStatus() in TeamSubscriptionPlan.tsx for
 * the "Renews" / "Expires" / "Cancelling" label that sits beside the date):
 *
 * | status               | endDate | cancelAtEndDate | plan                | state             | label      |
 * |----------------------|---------|-----------------|---------------------|-------------------|------------|
 * | (no subscription)    | —       | —               | —                   | None              | (hidden)   |
 * | active               | future  | false           | any                 | Valid             | Renews     |
 * | active               | future  | true            | B2C (Pro/Ess/…)     | Cancelling        | Cancelling |
 * | active               | future  | true            | B2B (Org/Complete)  | Valid             | Renews     |
 * | active               | past    | any             | any (incl. B2B)     | Expired           | Expired    |
 * | canceled             | past    | any             | any                 | Expired           | Expired    |
 * | canceled             | future  | any             | any                 | Expired           | Expired    |
 * | trialing             | future  | false           | any                 | Trial             | Renews     |
 * | trialing             | future  | true            | any                 | CancellingTrial   | Cancelling |
 * | trialing             | past    | any             | any                 | Trial / Cancelling­Trial (backend will soon flip to active/canceled) | Renews / Cancelling |
 * | past_due             | any     | any             | any                 | PastDue           | Expired    |
 * | disputed             | any     | any             | any                 | Disputed          | Renews     |
 * | unpaid / incomplete /|         |                 |                     |                   |            |
 * |   incomplete_expired |         |                 |                     |                   |            |
 * |   / paused / expired | any     | any             | any                 | Expired           | Expired    |
 */
export function calculateSubscriptionState(
  subscription: Pick<
    Subscription,
    'endDate' | 'cancelAtEndDate' | 'status' | 'plan'
  > | null
) {
  if (!subscription) return 'None' as const;

  const { endDate, cancelAtEndDate, status, plan } = subscription;

  // end_date is the authoritative signal for "still paid?". B2B subs never get
  // their status flipped to 'canceled' automatically, so checking end_date up
  // front catches them. Trials are excluded because their end_date is the
  // trial end, not the subscription end — the backend will move them to
  // active or canceled shortly after.
  if (
    isAfter(Date.now(), new Date(endDate)) &&
    status !== SubscriptionStatus.Trialing
  ) {
    return 'Expired' as const;
  }

  if (
    cancelAtEndDate &&
    status === SubscriptionStatus.Active &&
    ![PlanType.Organization, PlanType.Complete].includes(plan)
  ) {
    return 'Cancelling' as const;
  }

  if (status === SubscriptionStatus.Active) return 'Valid' as const;

  if (status === SubscriptionStatus.PastDue) return 'PastDue' as const;

  if (cancelAtEndDate && status === SubscriptionStatus.Trialing)
    return 'CancellingTrial' as const;

  if (status === SubscriptionStatus.Trialing) return 'Trial' as const;

  if (status === SubscriptionStatus.Disputed) return 'Disputed' as const;

  return 'Expired' as const;
}

export type SubscriptionState = ReturnType<typeof calculateSubscriptionState>;
